import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { steps } from "@components/FirstTimeUserTour/steps";
import { POPULATE_USER_SAMPLE_TEMPLATES } from "@graphql/mutations/PopulateUserSampleTemplates";
import { usePreferences, useTemplates } from "@hooks";
import { usePremiumFeature } from "@hooks/use-premium-feature";
import mixpanel from "mixpanel-browser";
import { useEffect, useMemo, useRef, useState } from "react";
import Joyride, { EVENTS, STATUS, ACTIONS } from "react-joyride";
import { useNavigate } from "react-router-dom";

export const FirstTimeUserTour = () => {
  const [run, setRun] = useState(false);
  const { firstTimeTourDisabled, setFirstTimeTourDisabled } =
    usePreferences();
  const [stepIndex, setStepIndex] = useState(0);
  const { refetchTemplates } = useTemplates();
  const [populateSampleTemplates] = useMutation(
    POPULATE_USER_SAMPLE_TEMPLATES,
  );
  const isResettingRef = useRef(false);
  const navigate = useNavigate();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const hasAdaptiveTemplates = hasFeatureAccess(
    UserPermissions.ADAPTIVE_TEMPLATES_ENABLED,
  );

  const activeSteps = useMemo(
    () =>
      hasAdaptiveTemplates
        ? steps
        : steps.filter((s) => !s.requiresAdaptiveTemplates),
    [hasAdaptiveTemplates],
  );

  useEffect(() => {
    if (firstTimeTourDisabled !== false) {
      return;
    }

    const currentStep = activeSteps[stepIndex];
    const nextStep = activeSteps[stepIndex + 1];
    let numIterations = 0;
    const interval = setInterval(() => {
      if (!run && document.querySelector(currentStep.target)) {
        if (stepIndex === 0) {
          mixpanel.track("First Time User Tour", {
            eventType: "Started",
          });
        }
        isResettingRef.current = false;
        setRun(true);
        return;
      }
      if (
        currentStep.waitIterations &&
        numIterations <= currentStep.waitIterations
      ) {
        numIterations += 1;
        return;
      }
      if (
        currentStep.abortTarget &&
        document.querySelector(currentStep.abortTarget)
      ) {
        mixpanel.track("First Time User Tour", {
          eventType: "Aborted",
          stepIndex,
          isAutomatic: true,
        });
        alert("error", currentStep.abortMessage);
        isResettingRef.current = true;
        clearInterval(interval);
        setRun(false);
        setStepIndex(0);
      } else if (
        (currentStep.eagerSkip &&
          document.querySelector(nextStep.target)) ||
        (currentStep.skipOnTargetLost &&
          !document.querySelector(currentStep.target))
      ) {
        mixpanel.track("First Time User Tour", {
          eventType: "Advanced",
          stepIndex,
          isAutomatic: true,
        });
        clearInterval(interval);
        setRun(false);
        setStepIndex(stepIndex + 1);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [stepIndex, firstTimeTourDisabled, run, activeSteps]);

  const handleCallback = async ({ action, index, status, type }) => {
    if (isResettingRef.current) return;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
      (action === ACTIONS.CLOSE && status === STATUS.RUNNING)
    ) {
      mixpanel.track("First Time User Tour", {
        eventType: action === ACTIONS.CLOSE ? "Closed" : "Finished",
        stepIndex,
      });
      setFirstTimeTourDisabled(true);
      setRun(false);
      setStepIndex(0);
    } else if (type === EVENTS.STEP_AFTER) {
      mixpanel.track("First Time User Tour", {
        eventType: "Advanced",
        stepIndex,
        isAutomatic: false,
      });
      if (
        action === ACTIONS.NEXT &&
        activeSteps[stepIndex].title === "Review Your Note" &&
        activeSteps[stepIndex].target === "body"
      ) {
        setRun(false);
        setStepIndex(index + 1);
        if (hasAdaptiveTemplates) {
          await populateSampleTemplates();
          await refetchTemplates();
        } else {
          navigate("/dashboard");
        }
        return;
      }
      setStepIndex(index + 1);
    }
  };

  return (
    <Joyride
      steps={activeSteps}
      continuous={true}
      hideBackButton={true}
      callback={handleCallback}
      disableOverlayClose={true}
      stepIndex={stepIndex}
      disableScrolling={true}
      run={run}
      locale={{ last: "Finish" }}
      styles={{
        options: {
          backgroundColor: "var(--surface-secondary)",
          textColor: "var(--text-primary)",
        },
        tooltipTitle: {
          color: "var(--text-primary)",
          padding: "0 10px",
        },
        tooltip: {
          border: "3px solid var(--border-brand)",
          borderRadius: "var(--ds-radius-standard)",
          textAlign: "left",
        },
        tooltipContainer: {
          textAlign: "left",
        },
        tooltipContent: {
          padding: "10px",
        },
        buttonNext: {
          background: "var(--surface-brand)",
          borderRadius: "9999px",
          padding: "4px 12px",
          fontSize: "16px",
          fontWeight: 500,
          lineHeight: "24px",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
        floaterStyles: {
          arrow: {
            color: "var(--surface-brand)",
          },
        },
      }}
    ></Joyride>
  );
};
